<script lang="ts" setup>
import CreateCheckCallPip from "@/views/trips/components/pipWIndows/CreateCheckCallPip.vue";
import type { TripMonitorRoute } from "@/api/trips/dto/trip-monitor";
import TripIntervalDialog from "@/views/trips/components/TripIntervalDialog.vue";
import { API } from "@/api/api";
import { useSnackbarStore } from "@/store/snackbar.store";
import { computed, ref, watch } from "vue";
import type { CheckCallDTO, CreateCheckCallRequest } from "@/api/trips/dto/check-call";
import { useError } from "@/composables/useError";

const api = new API();

const { showSnackbarSuccess, showSnackbarError } = useSnackbarStore();
const { showAPIError } = useError();

const showAddCheckCallDialog = defineModel<boolean>("showAddCheckCallDialog", { default: false });
const showConfirmDispatchDialog = defineModel<boolean>("showConfirmDispatchDialog", { default: false });
const showIntervalDialog = defineModel<boolean>("showIntervalDialog", { default: false });
const props = defineProps<{
  tripMonitorRoute: TripMonitorRoute;
  tripId: number;
}>();
const createCheckCallTitle = computed(() => {
  return "New check call for truck #" + props.tripMonitorRoute.truck.number;
});
const confirmDispatchTitle = computed(() => {
  return preview.value ? "Dispatch preview" : "Dispatch confirmation";
});

const checkCallPreview = ref<CheckCallDTO | null>(null);
const preview = ref<boolean>(false);
const creationLoading = ref<boolean>(false);

async function updateInterval(interval: number | null): Promise<void> {
  try {
    const response = await api.trips.updateRouteInterval(props.tripId, props.tripMonitorRoute.id, interval);

    if (response.success) {
      showSnackbarSuccess("Interval saved successfully");
    }
  } catch (error) {
    console.log(error);
    showSnackbarError("Could not save the trip interval");
  }
}

async function createCheckCall(data: CreateCheckCallRequest) {
  creationLoading.value = true;

  try {
    const response = await api.events.createCheckCall(props.tripMonitorRoute.id, data);

    if (!response.success || !response.data) {
      showAPIError(response, "Error creating check call");

      return;
    }

    if (!data.commit) {
      checkCallPreview.value = response.data;
    }
  } catch (error) {
    console.error(error);
  } finally {
    creationLoading.value = false;
  }
}

async function confirmDispatch(data: CreateCheckCallRequest) {
  creationLoading.value = true;

  try {
    const response = await api.trips.confirmRouteDispatch(props.tripId, props.tripMonitorRoute.id, data);

    if (!response.success || !response.data) {
      showAPIError(response, "Error dispatching route");

      return;
    }

    if (!data.commit) {
      checkCallPreview.value = response.data;
    }
  } catch (error) {
    console.error(error);
  } finally {
    creationLoading.value = false;
  }
}

watch([showAddCheckCallDialog, showConfirmDispatchDialog], () => {
  checkCallPreview.value = null;
  preview.value = false;
});
</script>

<template>
  <create-check-call-pip
    v-if="props.tripMonitorRoute && showAddCheckCallDialog"
    v-model="showAddCheckCallDialog"
    v-model:preview="preview"
    :check-call-preview="checkCallPreview"
    :creation-loading="creationLoading"
    :route-id="props.tripMonitorRoute.id"
    :route-stops="props.tripMonitorRoute.nextRouteStop ? [props.tripMonitorRoute.nextRouteStop] : []"
    :title="createCheckCallTitle"
    @create="createCheckCall"
  />

  <create-check-call-pip
    v-if="props.tripMonitorRoute && showConfirmDispatchDialog"
    v-model="showConfirmDispatchDialog"
    v-model:preview="preview"
    :check-call-preview="checkCallPreview"
    :creation-loading="creationLoading"
    :route-id="props.tripMonitorRoute.id"
    :route-stops="props.tripMonitorRoute.nextRouteStop ? [props.tripMonitorRoute.nextRouteStop] : []"
    :title="confirmDispatchTitle"
    :trip-id="props.tripId"
    is-dispatch-confirmation
    @create="confirmDispatch"
  >
    <template #subtitle>
      <div class="d-flex align-center mb-4">
        <span class="text-grey-darken-1"> You are about to confirm dispatch for</span>

        <router-link
          v-if="props.tripMonitorRoute.truck"
          :to="{ name: 'truck-view', params: { id: props.tripMonitorRoute.truck?.id } }"
          class="text-primary text-decoration-none ml-1"
          data-qa="modal-truck-number"
          target="_blank"
        >
          Truck #{{ props.tripMonitorRoute.truck?.number }}
        </router-link>
      </div>
    </template>
  </create-check-call-pip>

  <trip-interval-dialog
    v-if="showIntervalDialog"
    v-model="showIntervalDialog"
    :interval="props.tripMonitorRoute.checkCallInterval"
    :truck-id="props.tripMonitorRoute.truck.id"
    :truck-number="props.tripMonitorRoute.truck.number"
    title="Route timer settings"
    @update-interval="updateInterval"
  />
</template>
